import "cross-fetch/polyfill";

import { ApolloClient, createHttpLink, from, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { SchemaLink } from "@apollo/client/link/schema";
import { addMocksToSchema } from "@graphql-tools/mock";
import { makeExecutableSchema } from "@graphql-tools/schema";
import { GraphQLError } from "graphql";

import schemaSDL from "@/app/graphql/schema.graphql?raw";

import { APOLLO_GRAPH_VARIANT, FEDERATED_GATEWAY_URL, USE_GRAPHQL_DEMO_DATA } from "../../lib/env-helpers";
import { getSupportEmail } from "../../lib/get-support-email";
import i18n from "../../lib/i18n";
import { fallBackErrorMessage } from "../notifications";
import { typePolicies } from "./demo-data/type-policies";

export const errorLink = onError(({ graphQLErrors, response }) => {
  if (response)
    response.errors = [
      new GraphQLError(
        i18n.t((graphQLErrors?.[0]?.extensions?.errorCode as string) || fallBackErrorMessage, fallBackErrorMessage, {
          supportEmail: getSupportEmail(),
        }),
      ),
    ];
});

const mockHttpLink = new SchemaLink({
  schema: addMocksToSchema({
    schema: makeExecutableSchema({ typeDefs: schemaSDL }),
    mocks: {
      Query: () =>
        Object.entries(typePolicies.typePolicies?.Query.fields ?? {}).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: (...vars: any) => (value as { read: (...vars: any) => any }).read(...vars),
          }),
          {},
        ),
      Mutation: () => typePolicies.typePolicies?.Mutation?.fields,
      UUID: () => "00000000-0000-0000-0000-000000000000",
      Long: () => "0",
      Date: () => "2027-01-01",
      DateTime: () => "2027-01-01T00:00:00.000Z",
      BigDecimal: () => 0,
      PositiveInt: () => 1,
      JSON: () => ({}),
      LocalTime: () => "00:00:00",
    },
    preserveResolvers: false,
  }),
});

const mockInMemoryCache = new InMemoryCache();

const apolloClient = () => {
  const httpLink = USE_GRAPHQL_DEMO_DATA
    ? mockHttpLink
    : createHttpLink({
        uri: FEDERATED_GATEWAY_URL,
      });

  const authLink = setContext((_, { headers }) => {
    const accessToken: string = getAccessToken();
    return {
      headers: {
        ...headers,
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
    };
  });

  return new ApolloClient({
    link: from([authLink, errorLink, httpLink]),
    cache: USE_GRAPHQL_DEMO_DATA
      ? mockInMemoryCache
      : new InMemoryCache({
          typePolicies: {
            Asset: {
              keyFields: false,
            },
          },
        }),
    connectToDevTools: APOLLO_GRAPH_VARIANT === "development",
  });
};

export const getAccessToken = () => {
  const localStorageOktaToken = localStorage.getItem("okta-token-storage");
  if (localStorageOktaToken) {
    const oktaTokenStorage = JSON.parse(localStorageOktaToken);
    if (oktaTokenStorage.accessToken && oktaTokenStorage.accessToken.accessToken) {
      return oktaTokenStorage.accessToken.accessToken;
    }
  }
  return "";
};

export default apolloClient;
